import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Filter, Crown, Clock, DollarSign, 
  Calendar, Eye, Edit, Trash2, Download, Plus,
  CheckCircle, XCircle, AlertTriangle, MoreHorizontal, 
  ChevronDown, ChevronUp, ArrowRight, RefreshCw
} from 'lucide-react';
import AdminLayout from '../common/AdminLayout';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../utils/api';

const getImageUrl = (path: string | undefined | null): string | null => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const base = API_BASE_URL.replace(/\/api\/?$/, '');
  if (path.startsWith('/uploads/') || path.startsWith('uploads/')) {
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return `${base}/${cleanPath}`;
  }
  return `${base}/uploads/${path}`;
};

interface MembershipTier {
  id: number;
  name: string;
  display_name: string;
}

interface MembershipUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  profile_picture: string;
  city: string;
  state: string;
  membership_tier_id: number;
  membership_status: 'active' | 'expired' | 'cancelled';
  membership_start_date: string;
  membership_end_date: string;
  services_used: number;
  tier_name: string;
  tier_display_name: string;
  tier_price: number;
  duration_months: number;
  service_limit: number;
  transaction_count: number;
  total_spent: number;
}

interface Transaction {
  id: number;
  transaction_id: string;
  amount: number;
  currency: string;
  payment_method: string;
  payment_gateway: string;
  status: string;
  start_date: string;
  end_date: string;
  created_at: string;
  tier_name: string;
  tier_display_name: string;
}

interface UsageLog {
  id: number;
  action: string;
  description: string;
  created_at: string;
  service_title: string;
}

interface Pagination {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
}

// Add CSS styles for the component
const styles = `
  .membership-dashboard {
    background-color: var(--bg-light);
  }
  
  .membership-header {
    color: white;
    padding: 2rem;
    border-radius: 10px;
    margin-bottom: 1.5rem;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  
  .membership-header h2 {
  color: #000;
    margin: 0;
    font-size: 1.8rem;
    font-weight: 600;
  }
  
  .membership-header p {
    margin: 0.5rem 0 0 0;
    opacity: 0.9;
  }
  
  .stat-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1rem;
  }
  
  .stat-card {
    background: white;
    border-radius: 10px;
    padding: 1.25rem;
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    display: flex;
    align-items: center;
    gap: 1rem;
    min-width: 0;
  }
  
  .stat-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
  }
  
  .stat-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    margin-bottom: 0;
    flex-shrink: 0;
  }
  
  .stat-card:nth-child(1) .stat-icon {
    background-color: rgba(79, 70, 229, 0.1);
    color: #4f46e5;
  }
  
  .stat-card:nth-child(2) .stat-icon {
    background-color: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }
  
  .stat-card:nth-child(3) .stat-icon {
    background-color: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  }
  
  .stat-card:nth-child(4) .stat-icon {
    background-color: rgba(236, 72, 153, 0.1);
    color: #ec4899;
  }
  
  .stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
    word-break: break-all;
    line-height: 1.2;
  }
  
  .stat-label {
    color: var(--text-medium-gray);
    font-size: 0.875rem;
  }
  
  .admin-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
  }
  
  .admin-table th {
    background-color: #f9fafb;
    font-weight: 600;
    text-align: left;
    padding: 0.75rem 1rem;
    border-bottom: 2px solid #e5e7eb;
  }
  
  .admin-table td {
    padding: 1rem;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: middle;
  }
  
  .admin-table tr:hover td {
    background-color: #f9fafb;
  }
  
  .badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }
  
  .badge-success {
    background-color: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }
  
  .badge-warning {
    background-color: rgba(245, 158, 11, 0.1);
    color: #f59e0b;
  }
  
  .badge-danger {
    background-color: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
  
  .badge-info {
    background-color: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  }
  
  .badge-secondary {
    background-color: rgba(107, 114, 128, 0.1);
    color: #6b7280;
  }
  
  .btn-primary, .btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;
  }
  
  .btn-primary {
    background-color: #4f46e5;
    color: white;
    border: none;
  }
  
  .btn-primary:hover {
    background-color: #4338ca;
  }
  
  .btn-secondary {
    background-color: white;
    color: #374151;
    border: 1px solid #d1d5db;
  }
  
  .btn-secondary:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }
  
  /* Enhanced Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
    animation: fadeIn 0.2s ease;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideUp {
    from { transform: translateY(50px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  .modal-content {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 800px;
    max-height: 90vh;
    overflow-y: auto;
    animation: slideUp 0.3s ease;
    border: 1px solid rgba(0,0,0,0.05);
  }
  
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    background: linear-gradient(to right, #f8f9fa, #ffffff);
  }
  
  .modal-header h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .modal-title-icon {
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: white;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
  }
  
  .modal-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #6b7280;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }
  
  .modal-close:hover {
    background-color: #f3f4f6;
    color: #1f2937;
  }
  
  .modal-body {
    padding: 1.5rem;
  }
  
  .modal-footer {
    display: flex;
    justify-content: flex-end;
    padding: 1rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    gap: 0.75rem;
    background: #f9fafb;
  }
  
  .modal-tabs {
    display: flex;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 1.5rem;
    overflow-x: auto;
  }
  
  .modal-tab {
    padding: 0.75rem 1.25rem;
    font-weight: 500;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.2s;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .modal-tab.active {
    border-bottom-color: #4f46e5;
    color: #4f46e5;
  }
  
  .modal-tab:hover:not(.active) {
    background-color: #f9fafb;
    border-bottom-color: #d1d5db;
  }
  
  .modal-section {
    margin-bottom: 2rem;
  }
  
  .modal-section-title {
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .modal-section-icon {
    background-color: #f3f4f6;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
  }
  
  .user-detail-header {
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    color: white;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
  
  .user-avatar-large {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: 600;
    color: white;
    background-color: rgba(255, 255, 255, 0.2);
    border: 3px solid rgba(255, 255, 255, 0.5);
  }
  
  .user-detail-info h4 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0 0 0.25rem 0;
  }
  
  .user-detail-meta {
    opacity: 0.9;
    font-size: 0.875rem;
  }
  
  .tab-content {
    display: none;
  }
  
  .tab-content.active {
    display: block;
    animation: fadeIn 0.3s ease;
  }
  
  .user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
  }
  
  .user-info {
    background-color: #f9fafb;
    border-radius: 0.5rem;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
    margin-top: 1rem;
  }
  
  .info-item {
    background-color: white;
    border-radius: 0.5rem;
    padding: 1rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
  
  .info-label {
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 0.25rem;
  }
  
  .info-value {
    font-weight: 500;
  }
  
  .search-filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 0;
    width: 100%;
  }
  
  .filter-item {
    flex: 1;
    min-width: 160px;
  }
  
  .filter-item.search-box-wrapper {
    position: relative;
    flex: 1.5;
    min-width: 220px;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
    box-shadow: none !important;
    outline: none !important;
  }
  
  .search-filters .form-control,
  .search-filters select.form-control,
  .search-filters input.form-control {
    height: 42px !important;
    padding: 0 0.875rem !important;
    font-size: 0.875rem !important;
    line-height: 42px !important;
    border-radius: 8px !important;
    border: 1px solid #d1d5db !important;
    background-color: #ffffff !important;
    color: #1f2937 !important;
    box-shadow: none !important;
    transition: all 0.2s ease-in-out !important;
    box-sizing: border-box !important;
    width: 100% !important;
    margin: 0 !important;
    outline: none !important;
  }

  .search-filters select.form-control {
    appearance: none !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    padding-right: 2.25rem !important;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%6b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e") !important;
    background-position: right 0.75rem center !important;
    background-repeat: no-repeat !important;
    background-size: 1.25em 1.25em !important;
    cursor: pointer !important;
  }
  
  .search-filters .search-box-wrapper input.form-control {
    padding-left: 2.5rem !important;
  }
  
  .search-filters .form-control:focus {
    border-color: #4f46e5 !important;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15) !important;
  }

  .search-filters .form-control:hover {
    border-color: #9ca3af !important;
  }
  
  .search-filters .search-icon {
    position: absolute;
    left: 0.875rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    pointer-events: none;
    z-index: 2;
  }

  .search-filters-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
  }

  .search-filters-actions .btn-primary,
  .search-filters-actions .btn-secondary {
    height: 42px !important;
    padding: 0 1.25rem !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 0.5rem !important;
    transition: all 0.2s ease-in-out !important;
    box-sizing: border-box !important;
    margin: 0 !important;
    line-height: 1 !important;
    white-space: nowrap !important;
  }

  .search-filters-actions .btn-secondary {
    background-color: #ffffff !important;
    color: #374151 !important;
    border: 1px solid #d1d5db !important;
  }

  .search-filters-actions .btn-secondary:hover {
    background-color: #f9fafb !important;
    border-color: #9ca3af !important;
    color: #111827 !important;
  }

  .search-filters-actions .btn-primary {
    background: #000000 !important;
    color: #ffffff !important;
    border: 1px solid #000000 !important;
  }

  .search-filters-actions .btn-primary:hover {
    background: #1f2937 !important;
    border-color: #1f2937 !important;
  }
  
  .loading-spinner {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 22px;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .spinner {
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-left-color: #4f46e5;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    animation: spin 1s linear infinite;
  }
  
  .empty-state {
    text-align: center;
    padding: 3rem 1rem;
    color: #6b7280;
  }
  
  .empty-state-icon {
    margin-bottom: 1rem;
    color: #d1d5db;
  }
`;

const AdminMembershipManagement: React.FC = () => {
  const [memberships, setMemberships] = useState<MembershipUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1
  });
  const [availableTiers, setAvailableTiers] = useState<MembershipTier[]>([]);
  const [selectedUser, setSelectedUser] = useState<MembershipUser | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchMemberships();
  }, [pagination.page, searchTerm, statusFilter, tierFilter]);

  // Add this useEffect for the styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const fetchMemberships = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        search: searchTerm,
        status: statusFilter,
        tier: tierFilter
      });

      const response = await fetch(`${API_BASE_URL}/dashboard/membership-users?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_admin_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch membership users');
      }

      const data = await response.json();
      
   
      if (data.success) {
        setMemberships(data.memberships || []);
        setPagination({
          ...pagination,
          totalItems: data.pagination?.totalItems || 0,
          totalPages: data.pagination?.totalPages || 1
        });
        setAvailableTiers(data.filters?.tiers || []);
      } else {
        throw new Error(data.message || 'Failed to fetch membership users');
      }
    } catch (error) {
      console.error('Error fetching memberships:', error);
      setError('Error fetching membership data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId: number) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/membership-transactions/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_admin_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      const data = await response.json();
      
      if (data.success) {
        setTransactions(data.transactions || []);
        setUsageLogs(data.usageLogs || []);
        setShowUserDetails(true);
      } else {
        throw new Error(data.message || 'Failed to fetch user details');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to load user details',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewDetails = (user: MembershipUser) => {
    setSelectedUser(user);
    fetchUserDetails(user.id);
  };

  const handlePageChange = (newPage: number) => {
    setPagination({
      ...pagination,
      page: newPage
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const dateObj = new Date(dateString);
      if (isNaN(dateObj.getTime())) return String(dateString);
      const hasTime = dateObj.getHours() !== 0 || dateObj.getMinutes() !== 0 || dateObj.getSeconds() !== 0;
      if (hasTime) {
        return dateObj.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      }
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return String(dateString);
    }
  };

  const formatCurrency = (amount: any) => {
    const numericValue = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(isNaN(numericValue) ? 0 : numericValue);
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      active: 'badge-success',
      expired: 'badge-warning',
      cancelled: 'badge-danger',
      pending: 'badge-info',
      completed: 'badge-success',
      failed: 'badge-danger',
      refunded: 'badge-secondary'
    };
    
    return `badge ${statusClasses[status as keyof typeof statusClasses] || 'badge-secondary'}`;
  };

  const exportMemberships = () => {
    const csvContent = [
      ['ID', 'Name', 'Email', 'Tier', 'Status', 'Start Date', 'End Date', 'Services Used', 'Transactions', 'Total Spent'].join(','),
      ...memberships.map(user => [
        user.id,
        `${user.first_name} ${user.last_name}`,
        user.email,
        user.tier_display_name,
        user.membership_status,
        formatDate(user.membership_start_date),
        formatDate(user.membership_end_date),
        user.services_used,
        user.transaction_count,
        formatCurrency(user.total_spent || 0)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'membership_users.csv';
    a.click();
  };

  return (
    <AdminLayout title="Membership Management" subtitle="Manage user memberships and subscriptions">
      <div className="membership-dashboard fade-in">
        {/* Header Banner */}
        <div className="membership-header">
          <h2>Membership Management</h2>
          <p>Monitor and manage user memberships, transactions, and subscription analytics</p>
        </div>

        {/* Statistics Cards */}
        <div className="stat-cards" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <Users size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{pagination.totalItems}</div>
              <div className="stat-label">Total Members</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <CheckCircle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">
                {memberships.filter(m => m.membership_status === 'active').length}
              </div>
              <div className="stat-label">Active Memberships</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Clock size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">
                {memberships.filter(m => m.membership_status === 'expired').length}
              </div>
              <div className="stat-label">Expired</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <DollarSign size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">
                {formatCurrency(memberships.reduce((sum, m) => sum + (parseFloat(m.total_spent as any) || 0), 0))}
              </div>
              <div className="stat-label">Total Revenue</div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
          <div className="search-filters">
            <div className="filter-item search-box-wrapper">
              <input
                type="text"
                placeholder="Search members..."
                className="form-control"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search size={18} className="search-icon" />
            </div>

            <div className="filter-item">
              <select 
                className="form-control" 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="filter-item">
              <select 
                className="form-control" 
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
              >
                <option value="all">All Tiers</option>
                {availableTiers.map(tier => (
                  <option key={tier.id} value={tier.id}>{tier.display_name}</option>
                ))}
              </select>
            </div>

            <div className="search-filters-actions">
              <button className="btn-secondary" onClick={exportMemberships}>
                <Download size={18} />
                Export
              </button>
              <button className="btn-primary" onClick={fetchMemberships}>
                <RefreshCw size={18} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Memberships Table */}
        <div className="admin-card">
          <div className="card-header">
            <div className="card-title">Membership Users</div>
            <div style={{ fontSize: '14px', color: 'var(--text-medium-gray)' }}>
              Showing {memberships.length} of {pagination.totalItems} users
            </div>
          </div>

          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : memberships.length === 0 ? (
            <div className="empty-state">
              <Users size={48} className="empty-state-icon" />
              <h3>No membership users found</h3>
              <p>Try adjusting your filters or search criteria</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Membership</th>
                    <th>Status</th>
                    <th>Dates</th>
                    <th>Usage</th>
                    <th>Transactions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {memberships.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {user.profile_picture ? (
                            <img 
                              src={getImageUrl(user.profile_picture) || ''} 
                              alt={`${user.first_name} ${user.last_name}`}
                              className="user-avatar"
                              style={{ objectFit: 'cover' }}
                            />
                          ) : (
                            <div className="user-avatar" style={{ backgroundColor: 'var(--accent-blue)' }}>
                              {user.first_name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: '500' }}>
                              {user.first_name} {user.last_name}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Crown size={16} style={{ color: 'var(--accent-gold)' }} />
                          <span>{user.tier_display_name}</span>
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                          {formatCurrency(user.tier_price)} / {user.duration_months} months
                        </div>
                      </td>
                      <td>
                        <span className={getStatusBadge(user.membership_status)}>
                          {user.membership_status}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: '12px' }}>
                          <div><strong>Start:</strong> {formatDate(user.membership_start_date)}</div>
                          <div><strong>End:</strong> {formatDate(user.membership_end_date)}</div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontSize: '14px' }}>
                            <strong>{user.services_used}</strong> / {user.service_limit === -1 ? '∞' : user.service_limit}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                            services used
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontSize: '14px' }}>
                            <strong>{user.transaction_count}</strong> transactions
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                            {formatCurrency(user.total_spent || 0)} total
                          </div>
                        </div>
                      </td>
                      <td>
                        <button 
                          className="btn-secondary" 
                          style={{ padding: '0.5rem', minWidth: 'auto' }}
                          onClick={() => handleViewDetails(user)}
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: '1.5rem',
            padding: '1rem 0'
          }}>
            <div style={{ fontSize: '14px', color: 'var(--text-medium-gray)' }}>
              Page {pagination.page} of {pagination.totalPages}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="btn-secondary"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Previous
              </button>
              <button 
                className="btn-secondary"
                disabled={pagination.page === pagination.totalPages}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowUserDetails(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <div className="modal-title-icon">
                  <Crown size={18} />
                </div>
                Membership Details
              </h3>
              <button className="modal-close" onClick={() => setShowUserDetails(false)}>×</button>
            </div>
            
            {loadingDetails ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            ) : (
              <>
                {/* User Detail Header */}
                <div className="user-detail-header">
                  {selectedUser.profile_picture ? (
                    <img 
                      src={getImageUrl(selectedUser.profile_picture) || ''} 
                      alt={`${selectedUser.first_name} ${selectedUser.last_name}`}
                      className="user-avatar-large"
                      style={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <div className="user-avatar-large">
                      {selectedUser.first_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="user-detail-info">
                    <h4>{selectedUser.first_name} {selectedUser.last_name}</h4>
                    <div className="user-detail-meta">
                      {selectedUser.email} • {selectedUser.phone || 'No phone'}
                    </div>
                    <div className="user-detail-meta" style={{ marginTop: '0.25rem' }}>
                      {selectedUser.city}, {selectedUser.state}
                    </div>
                  </div>
                </div>
                
                <div className="modal-body">
                  {/* Tabs Navigation */}
                  <div className="modal-tabs">
                    <div className="modal-tab active">
                      <Crown size={16} />
                      Membership
                    </div>
                   
                  
                  </div>
                  
                  {/* Membership Tab Content */}
                  <div className="tab-content active">
                    <div className="modal-section">
                      <div className="modal-section-title">
                        <div className="modal-section-icon">
                          <Crown size={16} />
                        </div>
                        Membership Information
                      </div>
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">Tier</div>
                          <div className="info-value">{selectedUser.tier_display_name}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Status</div>
                          <div className="info-value">
                            <span className={getStatusBadge(selectedUser.membership_status)}>
                              {selectedUser.membership_status}
                            </span>
                          </div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Start Date</div>
                          <div className="info-value">{formatDate(selectedUser.membership_start_date)}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">End Date</div>
                          <div className="info-value">{formatDate(selectedUser.membership_end_date)}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Services Used</div>
                          <div className="info-value">{selectedUser.services_used} / {selectedUser.service_limit === -1 ? '∞' : selectedUser.service_limit}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Total Spent</div>
                          <div className="info-value">{formatCurrency(selectedUser.total_spent || 0)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Transactions Section */}
                    <div className="modal-section">
                      <div className="modal-section-title">
                        <div className="modal-section-icon">
                          <DollarSign size={16} />
                        </div>
                        Recent Transactions
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Transaction ID</th>
                              <th>Date</th>
                              <th>Amount</th>
                              <th>Status</th>
                              <th>Period</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.length > 0 ? transactions.slice(0, 3).map(tx => (
                              <tr key={tx.id}>
                                <td>{tx.transaction_id}</td>
                                <td>{formatDate(tx.created_at)}</td>
                                <td>{formatCurrency(tx.amount)}</td>
                                <td>
                                  <span className={getStatusBadge(tx.status)}>
                                    {tx.status}
                                  </span>
                                </td>
                                <td>
                                  {formatDate(tx.start_date)} - {formatDate(tx.end_date)}
                                </td>
                              </tr>
                            )) : (
                              <tr>
                                <td colSpan={5} className="empty-state" style={{ padding: '1rem' }}>
                                  <p>No transactions found</p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Usage Logs Section */}
                    <div className="modal-section">
                      <div className="modal-section-title">
                        <div className="modal-section-icon">
                          <Clock size={16} />
                        </div>
                        Recent Usage
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Action</th>
                              <th>Service</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usageLogs.length > 0 ? usageLogs.slice(0, 3).map(log => (
                              <tr key={log.id}>
                                <td>{formatDate(log.created_at)}</td>
                                <td>{log.action}</td>
                                <td>{log.service_title || 'N/A'}</td>
                                <td>{log.description || 'No description'}</td>
                              </tr>
                            )) : (
                              <tr>
                                <td colSpan={4} className="empty-state" style={{ padding: '1rem' }}>
                                  <p>No usage logs found</p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
               
              </>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminMembershipManagement;